import {
  detectPlatform,
  formatWithLabels,
  formatForDisplay,
  MODIFIER_ORDER,
  CanonicalModifier,
  Hotkey,
  parseHotkey,
} from "@tanstack/react-hotkeys";
import { MDSShortcutKeyComboPlatform } from "./shortcut-key.types";

const SENTINEL_KEY = "K";

const normalizeTokenText = (token: string): string =>
  token.length === 1 ? token.toUpperCase() : token;

// Component defaults to auto platform, but tests/stories can force one.
const getResolvedPlatform = (platform?: MDSShortcutKeyComboPlatform): MDSShortcutKeyComboPlatform =>
  platform ?? detectPlatform();

// "Sort" here is really an order projection: keep only modifiers present in canonical order.
const sortModifiersForDisplay = (modifiers: CanonicalModifier[]): CanonicalModifier[] =>
  MODIFIER_ORDER.filter(modifier => modifiers.includes(modifier));

const formatModifierToken = ({
  modifier,
  platform,
}: {
  modifier: CanonicalModifier;
  platform: MDSShortcutKeyComboPlatform;
}): string => {
  // Product display rule: use the command symbol on macOS, keep other modifiers as labels.
  if (platform === "mac" && modifier === "Meta") {
    const formattedCombo = formatForDisplay(`${modifier}+${SENTINEL_KEY}`, { platform });
    const formattedKey = formatForDisplay(SENTINEL_KEY, { platform });
    return formattedCombo.endsWith(formattedKey)
      ? formattedCombo.slice(0, -formattedKey.length).trimEnd()
      : formattedCombo.trimEnd();
  }

  const formattedCombo = formatWithLabels(`${modifier}+${SENTINEL_KEY}`, { platform });
  return formattedCombo.split("+")[0] ?? formattedCombo;
};

export const getShortcutKeyComboDisplayTokens = ({
  hotkey,
  platform,
}: {
  hotkey: Hotkey;
  platform?: MDSShortcutKeyComboPlatform;
}): string[] => {
  const resolvedPlatform = getResolvedPlatform(platform);

  try {
    const parsedHotkey = parseHotkey(hotkey, resolvedPlatform);
    // Let TanStack handle special key symbols/labels (Esc, arrows, etc).
    const keyToken = normalizeTokenText(
      formatForDisplay(parsedHotkey.key, { platform: resolvedPlatform })
    );
    const modifierTokens = sortModifiersForDisplay(parsedHotkey.modifiers).map(modifier =>
      formatModifierToken({ modifier, platform: resolvedPlatform })
    );

    return [...modifierTokens, keyToken];
  } catch {
    return [normalizeTokenText(hotkey)];
  }
};
